/* eslint-disable no-undef */
import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  Typography, Box, Alert, Tabs, Tab, Paper, Button, 
  Card, CardContent, IconButton, Slider, LinearProgress 
} from '@mui/material';
import { LoadingSpinner } from '@horwath-htl/shared-ui';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { format } from 'date-fns';
import { MarketReport, MarketPodcast } from '@horwath-htl/shared-types';
import { Download, Headphones, PlayArrow, Pause, VolumeUp } from '@mui/icons-material';
import { ApiClient } from '../../services/api';

interface LatestInsightsSectionProps {
  marketId: number;
}

interface ReportDownloadButtonProps {
  report: MarketReport;
  marketId: number;
}

const ReportDownloadButton: React.FC<ReportDownloadButtonProps> = ({ report, marketId }) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const apiClient = new ApiClient({ authenticatedFetch });

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get(
        `${API_URL}/api/marketcompass/markets/${marketId}/reports/${report.id}/download-url`
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Open download URL in new tab
      window.open(data.downloadUrl, '_blank');
    },
    onError: (error: any) => {
      console.error('Failed to download report:', error);
    }
  });

  // Format the report month to MMMM YYYY
  const formatReportMonth = (reportMonth: number): string => {
    const year = Math.floor(reportMonth / 100);
    const month = reportMonth % 100;
    return format(new Date(year, month - 1), 'MMMM yyyy');
  };

  return (
    <Button
      variant="contained"
      color="primary"
      fullWidth
      startIcon={<Download />}
      onClick={() => downloadMutation.mutate()}
      disabled={downloadMutation.isPending}
    >
      Download {formatReportMonth(report.reportMonth)} MarketCompass Report
    </Button>
  );
};

interface SimplePodcastPlayerProps {
  podcast: MarketPodcast;
}

const SimplePodcastPlayer: React.FC<SimplePodcastPlayerProps> = ({ podcast }) => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(podcast.durationSeconds || 0);
  const [volume, setVolume] = React.useState(1);
  const [streamUrl, setStreamUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const apiClient = new ApiClient({ authenticatedFetch });

  // Reset state when podcast changes
  React.useEffect(() => {
    setStreamUrl(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setError(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [podcast.id]);

  const streamMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get(
        `${API_URL}/api/marketcompass/markets/${podcast.marketId}/podcasts/${podcast.id}/stream-url`
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.streamUrl) {
        setError(null);
        setStreamUrl(data.streamUrl);
        if (audioRef.current) {
          audioRef.current.src = data.streamUrl;
          // Add a small delay before playing to ensure the source is set
          setTimeout(() => {
            if (audioRef.current) {
              audioRef.current.play().catch(error => {
                setIsPlaying(false);
                
                // Check error type
                if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
                  setError('Audio playback was blocked. Please try again.');
                } else {
                  setError('Failed to play audio. Please try again.');
                }
              });
            }
          }, 100);
          setIsPlaying(true);
        }
      }
    },
    onError: (_error: any) => {
      setIsPlaying(false);
      setError('Failed to load podcast. Please try again.');
    }
  });

  const handlePlayPause = () => {
    if (!streamUrl && !streamMutation.isPending) {
      streamMutation.mutate();
    } else if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (event: Event, value: number | number[]) => {
    const time = value as number;
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const handleVolumeChange = (event: Event, value: number | number[]) => {
    const vol = value as number;
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onError={(e) => {
          const audio = e.currentTarget as HTMLAudioElement;
          
          // Reset playing state on error
          setIsPlaying(false);
          
          // Common error codes:
          // 1 = MEDIA_ERR_ABORTED
          // 2 = MEDIA_ERR_NETWORK
          // 3 = MEDIA_ERR_DECODE
          // 4 = MEDIA_ERR_SRC_NOT_SUPPORTED
          if (audio.error?.code === 4) {
            setError('Unable to load audio file. Please try again.');
          } else if (audio.error?.code === 2) {
            setError('Network error loading audio. Please check your connection.');
          } else {
            setError('Failed to load audio. Please try again.');
          }
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={handlePlayPause}
          disabled={streamMutation.isPending}
          color="primary"
        >
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>

        <Box sx={{ flex: 1, mx: 2 }}>
          <Slider
            value={currentTime}
            max={duration}
            onChange={handleSeek}
            disabled={!streamUrl}
            size="small"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">
              {formatDuration(currentTime)}
            </Typography>
            <Typography variant="caption">
              {formatDuration(duration)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', width: 120 }}>
          <VolumeUp sx={{ mr: 1 }} />
          <Slider
            value={volume}
            max={1}
            step={0.1}
            onChange={handleVolumeChange}
            size="small"
          />
        </Box>
      </Box>

      {streamMutation.isPending && (
        <LinearProgress sx={{ mt: 2 }} />
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
};

export const LatestInsightsSection: React.FC<LatestInsightsSectionProps> = ({ marketId }) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [selectedPeriod, setSelectedPeriod] = React.useState<number | null>(null);
  
  // Fetch reports data
  const { data: reportsData, isLoading: isLoadingReports, error: reportsError } = useQuery({
    queryKey: ['market-reports-insights', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/markets/${marketId}/reports`);
      
      if (!response) {
        throw new Error('No response from authenticatedFetch');
      }
      
      if (!response.ok) {
        const error: any = new Error(`HTTP ${response.status}`);
        error.response = { status: response.status };
        throw error;
      }
      
      const result = await response.json();
      return result;
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 403 (access denied)
      if (error?.response?.status === 403) return false;
      return failureCount < 3;
    }
  });

  // Fetch podcasts data
  const { data: podcastsData, isLoading: isLoadingPodcasts, error: podcastsError } = useQuery({
    queryKey: ['market-podcasts-insights', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/markets/${marketId}/podcasts`);
      
      if (!response) {
        throw new Error('No response from authenticatedFetch');
      }
      
      if (!response.ok) {
        const error: any = new Error(`HTTP ${response.status}`);
        error.response = { status: response.status };
        throw error;
      }
      
      const result = await response.json();
      return result;
    },
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 403) return false;
      return failureCount < 3;
    }
  });

  // Group reports by period and get unique periods sorted by most recent first
  const reportsByPeriod = React.useMemo(() => {
    if (!reportsData?.reports) return new Map();
    
    
    const grouped = new Map<number, MarketReport[]>();
    reportsData.reports.forEach((report: MarketReport) => {
      const existing = grouped.get(report.reportMonth) || [];
      grouped.set(report.reportMonth, [...existing, report]);
    });
    
    // Sort by period (most recent first)
    return new Map(Array.from(grouped.entries()).sort((a, b) => b[0] - a[0]));
  }, [reportsData?.reports]);

  // Group podcasts by period
  const podcastsByPeriod = React.useMemo(() => {
    if (!podcastsData?.podcasts) return new Map();
    
    const grouped = new Map<number, MarketPodcast[]>();
    podcastsData.podcasts.forEach((podcast: MarketPodcast) => {
      const existing = grouped.get(podcast.reportMonth) || [];
      grouped.set(podcast.reportMonth, [...existing, podcast]);
    });
    
    // Sort by period (most recent first)
    return new Map(Array.from(grouped.entries()).sort((a, b) => b[0] - a[0]));
  }, [podcastsData?.podcasts]);

  // Get sorted periods (combine from both reports and podcasts)
  const periods = React.useMemo(() => {
    const allPeriods = new Set<number>();
    reportsByPeriod.forEach((_, period) => {
      // Only add valid periods (YYYYMM format)
      if (period && period > 190000 && period < 210000) {
        const month = period % 100;
        if (month >= 1 && month <= 12) {
          allPeriods.add(period);
        }
      }
    });
    podcastsByPeriod.forEach((_, period) => {
      // Only add valid periods (YYYYMM format)
      if (period && period > 190000 && period < 210000) {
        const month = period % 100;
        if (month >= 1 && month <= 12) {
          allPeriods.add(period);
        }
      }
    });
    return Array.from(allPeriods).sort((a, b) => b - a);
  }, [reportsByPeriod, podcastsByPeriod]);

  // Set default selected period to most recent
  React.useEffect(() => {
    if (periods.length > 0 && selectedPeriod === null) {
      setSelectedPeriod(periods[0]);
    }
  }, [periods, selectedPeriod]);

  const formatPeriodLabel = (period: number): string => {
    // Validate the period value
    if (!period || isNaN(period) || period <= 0) {
      return 'Unknown Period';
    }
    
    const year = Math.floor(period / 100);
    const month = period % 100;
    
    // Validate year and month
    if (year < 1900 || year > 2100 || month < 1 || month > 12) {
      return 'Invalid Period';
    }
    
    try {
      return format(new Date(year, month - 1), 'MMMM yyyy');
    } catch (_error) {
      void _error;
      return `${month}/${year}`;
    }
  };

  const isLoading = isLoadingReports || isLoadingPodcasts;
  const error = reportsError || podcastsError;

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    const isAccessDenied = (error as any)?.response?.status === 403;
    return (
      <Alert severity={isAccessDenied ? "warning" : "error"}>
        {isAccessDenied
          ? "You don't have an active subscription for this market"
          : "Failed to load insights"}
      </Alert>
    );
  }

  const reports = reportsData?.reports || [];
  const podcasts = podcastsData?.podcasts || [];
  const marketIdFromResponse = reportsData?.marketId || podcastsData?.marketId;

  if (reports.length === 0 && podcasts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No insights available for this market yet
        </Typography>
      </Box>
    );
  }

  // Check if we have any valid periods
  if (periods.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No valid report periods available
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please contact support if this issue persists
        </Typography>
      </Box>
    );
  }

  // If only one period, show insights directly without tabs
  if (periods.length === 1) {
    const periodReports = reportsByPeriod.get(periods[0]) || [];
    const periodPodcasts = podcastsByPeriod.get(periods[0]) || [];
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Long descriptions with download buttons */}
        {periodReports.map((report: MarketReport) => (
          <Box key={`desc-${report.id}`}>
            <Box sx={{ mb: 2 }}>
              <ReportDownloadButton report={report} marketId={marketIdFromResponse || marketId} />
            </Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Report Summary
            </Typography>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {report.longDescription || 'No detailed insights available for this period.'}
              </Typography>
            </Paper>
          </Box>
        ))}
        
        {/* Podcasts */}
        {periodPodcasts.length > 0 && (
          <>
            {periodPodcasts.map((podcast: MarketPodcast) => (
              <Card key={`podcast-${podcast.id}`}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Headphones sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                      Listen to a short podcast
                    </Typography>
                  </Box>
                  <SimplePodcastPlayer podcast={{ ...podcast, marketId: marketIdFromResponse || marketId }} />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>
    );
  }

  // Multiple periods - show tabs
  const currentPeriodReports = selectedPeriod ? reportsByPeriod.get(selectedPeriod) || [] : [];
  const currentPeriodPodcasts = selectedPeriod ? podcastsByPeriod.get(selectedPeriod) || [] : [];

  // Guard against empty periods
  if (periods.length === 0) {
    return null;
  }

  return (
    <Box>
      <Tabs
        value={selectedPeriod || periods[0]}
        onChange={(_, newValue) => setSelectedPeriod(newValue)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {periods.map((period) => (
          <Tab
            key={period}
            label={formatPeriodLabel(period)}
            value={period}
          />
        ))}
      </Tabs>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Long descriptions with download buttons */}
        {currentPeriodReports.map((report: MarketReport) => (
          <Box key={`desc-${report.id}`}>
            <Box sx={{ mb: 2 }}>
              <ReportDownloadButton report={report} marketId={marketIdFromResponse || marketId} />
            </Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Report Summary
            </Typography>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {report.longDescription || 'No detailed insights available for this period.'}
              </Typography>
            </Paper>
          </Box>
        ))}
        
        {/* Podcasts */}
        {currentPeriodPodcasts.length > 0 && (
          <>
            {currentPeriodPodcasts.map((podcast: MarketPodcast) => (
              <Card key={`podcast-${podcast.id}`}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Headphones sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                      Listen to a short podcast
                    </Typography>
                  </Box>
                  <SimplePodcastPlayer podcast={{ ...podcast, marketId: marketIdFromResponse || marketId }} />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};